import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { PlayArrow, Stop } from "@mui/icons-material";
import sh from "bundles/common/utils/sh";

const FONT = "Segoe UI, sans-serif, system-ui";
const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";
const TIME_PROJECT_CHANGED_EVENT = "time-project:changed";
const DEFAULT_PROJECT_COLOR = "#3b82f6";

function emitTimeEntryChanged(detail) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TIME_ENTRY_CHANGED_EVENT, { detail }));
  }
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export default function TimerBar({ onChange }) {
  const authenticity_token = useSelector((s) => s.authenticity_token);

  const [projects, setProjects] = useState([]);
  const [running, setRunning] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [note, setNote] = useState("");
  const [noteFocused, setNoteFocused] = useState(false);
  const [now, setNow] = useState(Date.now());
  const seededRef = useRef(false);

  const fetchRunning = useCallback(() => {
    sh.get("/internal/time_entries/running").then((res) => {
      setRunning(Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : null);
    });
  }, []);

  useEffect(() => { fetchRunning(); }, [fetchRunning]);

  const fetchProjects = useCallback(() => {
    sh.get("/internal/time_projects?caller=timerbar").then((res) => setProjects(res.data || []));
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    const handler = () => fetchProjects();
    window.addEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
  }, [fetchProjects]);


  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (detail?.type === "start" && detail.entry) {
        setRunning(detail.entry);
        setNow(new Date(detail.entry.started_at).getTime());
        return;
      }
      if (detail?.type === "stop") {
        setRunning(null);
        seededRef.current = false;
        return;
      }
      fetchRunning();
    };
    window.addEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
  }, [fetchRunning]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (seededRef.current || !running) return;
    setSelectedProjectId(running.project?.id || "");
    setSelectedTaskId(running.task?.id || "");
    setNote(running.note || "");
    seededRef.current = true;
  }, [running]);

  const coloredProjects = useMemo(
    () => projects.map((p) => ({ ...p, color: p.color || DEFAULT_PROJECT_COLOR })),
    [projects],
  );
  const selectedProject = coloredProjects.find((p) => p.id === selectedProjectId) || null;

  const elapsed = running ? Math.max(0, Math.floor((now - new Date(running.started_at).getTime()) / 1000)) : 0;
  const isRunning = !!running;

  const startTimer = () => {
    if (!selectedProjectId || !selectedTaskId) return;
    setNow(Date.now());
    sh.post("/internal/time_entries", {
      authenticity_token,
      time_entry: {
        time_project_id: selectedProjectId,
        time_task_id: selectedTaskId,
        ...(note && { note }),
      },
    }).then((res) => {
      setNow(new Date(res.data.started_at).getTime());
      setRunning(res.data);
      onChange?.();
      emitTimeEntryChanged({ type: "start", entry: res.data });
    });
  };

  const stopTimer = () => {
    if (!running) return;
    const entryId = running.id;
    sh.patch(`/internal/time_entries/${running.id}/stop`, {
      authenticity_token,
      time_entry: { note: note || "" },
    }).then(() => {
      setRunning(null);
      seededRef.current = false;
      onChange?.();
      emitTimeEntryChanged({ type: "stop", entryId });
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: "1px solid hsl(140 8% 88%)",
        backgroundColor: "#ffffff",
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        position: "relative",
        overflow: "visible",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: 40, flexShrink: 0 }}>
          {isRunning && (
            <Box
              sx={{
                width: 8, height: 8, borderRadius: "50%",
                bgcolor: "hsl(145, 60%, 45%)",
                "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
                animation: "blink 1.4s infinite",
              }}
            />
          )}
          <Typography
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 28,
              lineHeight: 1,
              color: "hsl(150 30% 12%)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: -0.5,
            }}
          >
            {formatDuration(elapsed)}
          </Typography>
        </Box>

        <Select
          value={selectedProjectId}
          onChange={(e) => { setSelectedProjectId(e.target.value); setSelectedTaskId(""); }}
          displayEmpty
          size="small"
          variant="outlined"
          disabled={isRunning}
          sx={{
            minWidth: 220, height: 40, fontFamily: FONT, fontSize: 14,
            backgroundColor: "hsl(140 10% 95%)",
            borderRadius: "12px",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(150 38% 17%)" },
            "&.Mui-focused": { backgroundColor: "#fff" },
          }}
        >
          <MenuItem value="" disabled>
            <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 10% 46%)" }}>Select project</Typography>
          </MenuItem>
          {coloredProjects.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                  {p.name}{p.company?.name ? ` — ${p.company.name}` : ""}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>

        <Select
          value={selectedTaskId}
          onChange={(e) => setSelectedTaskId(e.target.value)}
          displayEmpty
          size="small"
          variant="outlined"
          disabled={isRunning || !selectedProjectId}
          sx={{
            minWidth: 180, height: 40, fontFamily: FONT, fontSize: 14,
            backgroundColor: "hsl(140 10% 95%)",
            borderRadius: "12px",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(150 38% 17%)" },
            "&.Mui-focused": { backgroundColor: "#fff" },
          }}
        >
          <MenuItem value="">
            <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 10% 46%)" }}>No task</Typography>
          </MenuItem>
          {(selectedProject?.tasks || []).map((t) => (
            <MenuItem key={t.id} value={t.id}>
              <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>{t.name.replace(/_/g, " ")}</Typography>
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ position: "relative", flex: 1, minWidth: 200, height: 40 }}>
          <InputBase
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onFocus={() => setNoteFocused(true)}
            onBlur={() => setNoteFocused(false)}
            placeholder="What are you working on?"
            multiline
            minRows={1}
            maxRows={noteFocused ? 10 : 1}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              px: 1.5,
              py: noteFocused ? 1 : 0,
              minHeight: 40,
              alignItems: noteFocused ? "flex-start" : "center",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              fontSize: 14,
              backgroundColor: "hsl(140 10% 95%)",
              border: "1px solid hsl(140 8% 88%)",
              borderRadius: "12px",
              color: "hsl(150 30% 12%)",
              boxShadow: noteFocused ? "0 8px 24px hsla(150, 30%, 12%, 0.14)" : "none",
              zIndex: noteFocused ? 5 : 1,
              transition: "min-height 0.18s ease, padding 0.18s ease, background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
              "& textarea": {
                resize: noteFocused ? "vertical" : "none",
                minHeight: noteFocused ? 72 : "auto",
              },
              "&.Mui-focused": { borderColor: "hsl(150 38% 17%)", backgroundColor: "#fff" },
            }}
          />
        </Box>

        <IconButton
          onClick={isRunning ? stopTimer : startTimer}
          disabled={!isRunning && (!selectedProjectId || !selectedTaskId)}
          aria-label={isRunning ? "Stop" : "Start"}
          sx={{
            width: 44, height: 44, borderRadius: "50%",
            bgcolor: isRunning ? "hsl(0, 78%, 60%)" : "hsl(150 38% 17%)",
            color: isRunning ? "#fff" : "hsl(80 97% 72%)",
            "&:hover": { bgcolor: isRunning ? "hsl(0, 78%, 55%)" : "hsla(150, 38%, 17%, 0.9)" },
            "&.Mui-disabled": { bgcolor: "hsl(140 8% 88%)", color: "hsl(150 10% 60%)" },
          }}
        >
          {isRunning ? <Stop sx={{ fontSize: 18 }} /> : <PlayArrow />}
        </IconButton>
      </Box>
    </Paper>
  );
}
